/**
 * @module app.shared
 */

import { autoinject } from 'aurelia-framework';
import { AppLogger } from 'app/shared/lib/logging/app-logger';
import { EnvironmentService } from 'app/shared/services/environment-service';
import { ISessionData, ISessionTenant, ISessionLocation, ISessionUser, ISessionUpdate, SessionChangeEventKey, ISessionRemember, ISessionUserContext, ISessionExpiration, SessionExpirationState, ISessionApplication } from 'app/shared/lib/session/session-types';
import { IStorageSubscription } from 'app/shared/lib/client-storage';
import { EventAggregator } from 'aurelia-event-aggregator';
import { SessionChangedEvent, SessionExpiringEvent, SessionExpiringEventData } from 'app/shared/lib/session/session-events';
import { TickHandler, TickService } from 'app/shared/services/event-services';
import { DateTimeValue } from 'app/shared/lib/date-time';

const SESSION_STORAGE_KEY = 'session';
const REMEMBER_STORAGE_KEY = 'remember';

const defaultSessionData: ISessionData = {
    event: 'startup',
    locked: false,
    application: null,
    tenant: null,
    user: null,
    context: null,
    location: null,
    access: null,
    refresh: null,
    client: null,
    authenticationCode: null,
    availableTenants: [],
    isSso: false
};

const defaultSessionRemember: ISessionRemember = {
    enabled: false,
    tenant: '',
    location: '',
    context: ''
};

function addSeconds(date: Date, seconds: number): string {
    const result = new Date(date.getTime());
    result.setUTCSeconds(result.getUTCSeconds() + seconds);
    return result.toISOString();
}

function isExpired(date: string | null): boolean {
    if(!date) {
        return true;
    }
    const dateValue = new Date(date); // parse iso date
    if(isNaN(dateValue.getTime())) {
        return true;
    }
    const now = new Date(Date.now());
    if(now.getTime() >= dateValue.getTime()) {
        return true;
    }
    return false;
}

@autoinject
export class AppSession {

    public expiration: ISessionExpiration = { isAlerted: false, seconds: 0, state: 'invalid', timestamp: '' };

    private expirationLimit: number;
    private data: ISessionData = defaultSessionData;
    private storageSubscription: IStorageSubscription;
    private isTimerStarted: boolean = false;
    private tickHandler: TickHandler;
    private expirationEventIsAlerted: boolean = false;
    private expirationEventIsWarned: boolean = false;
    private isSessionExpiring: boolean = false;

    constructor(
        private readonly env: EnvironmentService,
        private readonly logger: AppLogger,
        private readonly events: EventAggregator,
        private readonly ticker: TickService,
    ) {
        this.logger.source = this;
        this.logger.target = 'console';

        // load (allow loading from session in case this was a refresh)
        this.load(true);
        this.configureEnvironment();

        this.env.registerDebugObject('session', this);
        this.env.registerDebugObject('lock', () => {
            this.updateLocked(true);
            this.save('lock');
            this.configureEnvironment();
        });

        this.storageSubscription = this.env.storage.subscribe<ISessionData>(SESSION_STORAGE_KEY, (e) => {
            this.sync(e.newValue)
        });

        // convert minutes to seconds and change 0 to infinity
        this.expirationLimit =
            (this.env.config.session.minutesToExpirationOverride * 60 || Number.POSITIVE_INFINITY);

        // tick handler
        this.tickHandler = (e) => this.timerTick(e.time);
    }

    public get availableTenants(): ISessionTenant[] {
        return this.data.availableTenants || [];
    }

    public get tenant(): string | null {
        return this.data.tenant && this.data.tenant.code && this.data.tenant.code.toLowerCase() || null;
    }

    public get location(): string | null {
        return this.data.location && this.data.location.code && this.data.location.code.toLowerCase() || null;
    }

    public get context(): string | null {
        return this.data.context && this.data.context.id || null;
    }

    public get refreshToken(): string | null {
        return this.data.refresh && this.data.refresh.token || null;
    }

    public get accessToken(): string | null {
        return this.data.access && this.data.access.token || null;
    }

    public get clientToken(): string | null {
        return this.data.client && this.data.client.token || null;
    }

    public get authenticationCode(): string | null {
        return this.data.authenticationCode;
    }

    public get isSso(): boolean {
        return this.data.isSso || false;
    }
    public set isSso(v: boolean) {
        this.data.isSso = v;
    }


    public remember(state: Partial<ISessionRemember>): void {
        const rememberState = { ...defaultSessionRemember, ...state };
        this.env.storage.setObject(REMEMBER_STORAGE_KEY, rememberState);
    }

    public getRememberedState(): ISessionRemember {
        return { ...defaultSessionRemember, ...this.env.storage.getObject(REMEMBER_STORAGE_KEY) };
    }

    public shutdown(): void {
        this.logger.info('Shutting down session.');
        this.stopTimer();
        if(this.storageSubscription) {
            // stop listening
            this.storageSubscription.dispose();
        }
        // temporarily store state in session in case this is a refresh
        this.env.storage.setObject(SESSION_STORAGE_KEY, this.data, 'session');
        // completely remove from local storage
        this.env.storage.remove(SESSION_STORAGE_KEY);
    }

    public load(checkSessionStorage: boolean = false): boolean {
        this.logger.info('Loading Session...');

        let storedData = this.env.storage.getObject<ISessionData>(SESSION_STORAGE_KEY);
        let transfer: boolean = false;
        if(!storedData && checkSessionStorage) {
            this.logger.info('Checking for previous Session...');
            storedData = this.env.storage.getObject<ISessionData>(SESSION_STORAGE_KEY, 'session');
            if(storedData) {
                this.logger.info('Reloaded previous Session after refresh.');
                // remove temporary state from session storage
                this.env.storage.remove(SESSION_STORAGE_KEY, 'session');
                transfer = true;
            }
        }
        else {
            this.logger.info('Found current Session.');
        }

        let result: boolean = false;
        if(!storedData) {
            this.data = { ...defaultSessionData };
        }
        else {
            this.data = { ...defaultSessionData, ...storedData };
            if(!this.isLoggedIn()) {
                // clear tokens if they are no longer valid
                this.clear('expire');
                transfer = false;
            }
            result = true;
        }

        if(transfer) {
            // transfer to localStorage (without calling save--no need to trigger events)
            this.env.storage.setObject(SESSION_STORAGE_KEY, storedData);
        }

        return result;
    }

    // Updates the session cache.
    public save(event: SessionChangeEventKey): boolean {
        this.logger.info(`Saving Session... (${event})`);

        // store event so that it synchronizes with other tabs/windows
        this.data.event = event;
        this.env.storage.setObject(SESSION_STORAGE_KEY, this.data);

        this.events.publish(new SessionChangedEvent(event));

        return true;
    }

    private sync(sessionData?: ISessionData): void {
        // other tab saved session data
        if(sessionData) {
            //this.updateTenant()
            this.logger.info(`Session Data changed in other tab/window (${sessionData.event})`);
            this.data = sessionData;
            this.configureEnvironment();
            this.events.publish(new SessionChangedEvent(this.data.event));
        }
    }

    // Clears session data and updates the session cache.
    public clear(event: SessionChangeEventKey): void {
        this.logger.info(`Clearing Session (${event})...`);
        this.data.locked = false;
        this.data.authenticationCode = null;
        this.data.access = this.data.refresh = null;
        this.expiration = {
            isAlerted: false,
            timestamp: '',
            seconds: 0,
            state: 'invalid'
        };
        this.data.availableTenants = [];
        this.data.context = null;
        this.data.location = null;
        this.data.tenant = null;
        this.data.user = null;
        this.data.isSso = false;
        this.save(event);
    }

    public configureEnvironment(force: boolean = false): boolean {
        const isLoggedIn = this.isLoggedIn();
        const locked = this.data.locked;
        const application = isLoggedIn ? this.data.application : null;
        const tenant = isLoggedIn ? this.data.tenant : null;
        const user = isLoggedIn ? this.data.user : null;
        const context = isLoggedIn ? this.data.context : null;
        const location = isLoggedIn ? this.data.location : null;

        const update: Partial<ISessionUpdate> = {};
        let changes: boolean = false;

        if(force || this.env.locked !== locked) {
            update.locked = locked;
            changes = true;
        }

        if(application) {
            if(force || this.env.application.id !== application.id) {
                update.application = application;
                changes = true;
            }
        }
        if(tenant) {
            if(force || this.env.tenant.id !== tenant.id) {
                update.tenant = tenant;
                changes = true;
            }
        }
        else {
            if(this.env.tenant.isSelected) {
                update.tenant = null;
                changes = true;
            }
        }

        if(user) {
            // Note: Checking the Home Page changing is a bit of a workaround, since
            // when Checking In to a User Context (Provider), the User ID will not
            // change but the Home Page will.
            //
            // The long-term fix would be to move home page to env.context, since it's
            // Technically a part of the User Context and not the User, but checking
            // for a change at this step is the easier fix and code freeze is at
            // the end of the week. :sweat_smile: [Matthew J]
            //
            // Note: Additional workaround for time-zone change (time zone might change
            // in another tab, so we need to check for that here, too) [Matthew J]

            if(force || this.env.user.id !== user.id ||
                this.env.user.homePage.id !== user.homePage.id /*workaround*/ ||
                this.env.user.timeZone !== user.timeZone /*workaround*/) {
                update.user = user;
                changes = true;
            }
        }
        else {
            if(this.env.user.isLoggedIn) {
                update.user = null;
                changes = true;
            }
        }

        if(context) {
            if(force || this.env.context.id !== context.id) {
                update.context = context;
                changes = true;
            }
        }
        else {
            if(this.env.context.isSelected) {
                update.context = null;
                changes = true;
            }
        }

        if(location) {
            if(force || this.env.location.id !== location.id) {
                update.location = location;
                changes = true;
            }
        }
        else {
            if(this.env.location.isSelected) {
                update.location = null;
                changes = true;
            }
        }

        if(changes) {
            this.env.updateSession(update);
        }
        return changes;
    }

    public updateAvailableTenants(tenants: ISessionTenant[] = []): void {
        this.data.availableTenants = [...tenants];
    }

    public updateApplication(application: ISessionApplication | null): void {
        this.data.application = application;
    }

    public updateTenant(tenant: ISessionTenant | null): void {
        this.data.tenant = tenant;
    }

    public updateUser(user: ISessionUser | null): void {
        this.data.user = user;
    }

    public updateUserContext(userContext: ISessionUserContext | null): void {
        this.data.context = userContext;
    }

    public updateLocation(location: ISessionLocation | null): void {
        this.data.location = location;
    }

    public updateLocked(locked: boolean): void {
        this.data.locked = locked;
    }

    public updateAuthenticationCode(code: string): void {
        this.data.authenticationCode = code || null;
    }

    public updateAccessToken(accessToken: string, accessExpirationSeconds: number): void {
        this.logger.debug('Updating Access Token...');
        if(!accessToken) {
            this.data.access = null;
            return;
        }
        const seconds = Math.min(accessExpirationSeconds, this.expirationLimit); // apply override setting
        const now = new Date(Date.now());
        this.data.access = {
            token: accessToken,
            expiration: addSeconds(now, seconds || 0)
        };
    }

    public updateRefreshToken(refreshToken: string, refreshExpirationSeconds: number): void {
        this.logger.debug('Updating Refresh Token...');
        if(!refreshToken) {
            this.data.refresh = null;
            return;
        }
        const seconds = Math.min(refreshExpirationSeconds, this.expirationLimit); // apply override setting
        const now = new Date(Date.now());
        this.data.refresh = {
            token: refreshToken,
            expiration: addSeconds(now, seconds || 0)
        };
    }

    public updateClientToken(clientToken: string, clientExpirationSeconds: number): void {
        this.logger.debug('Updating Client Token...');
        if(!clientToken) {
            this.data.client = null;
            return;
        }
        const seconds = clientExpirationSeconds;
        const now = new Date(Date.now());
        this.data.client = {
            token: clientToken,
            expiration: addSeconds(now, seconds || 0)
        };
    }

    /** Session is valid if API is logged in and does not need refresh */
    public isValid(): boolean {
        return this.isLoggedIn() && !this.needsRefresh();
    }

    /** Session is logged in and refresh token exists and refresh token is not expired */
    public isLoggedIn(): boolean {
        return (this.data.refresh !== null && !isExpired(this.data.refresh.expiration));
    }

    public isCheckedIn(): boolean {
        return (this.data.context && this.data.context.isSelected) || false;
    }

    /** Session is Locked and must be Unlocked with the User's Password */
    public isLocked(): boolean {
        return !this.isLoggedIn() || !!this.data.locked;
    }

    /** This computer/client is trusted and can bypass Two-Factor Authentication */
    public isClientTrusted(): boolean {
        return (this.data.client !== null && !isExpired(this.data.client.expiration));
    }

    /** Session needs refreshing if API is logged and any of the following: no user information loaded, no access token, or expired access token  */
    public needsRefresh(): boolean {
        return this.isLoggedIn() && (this.data.user === null || this.data.access === null || isExpired(this.data.access.expiration));
    }

    public startTimer(): void {
        if(this.isTimerStarted) { return; }
        this.logger.info('Session Context Started');
        this.ticker.addEventListener('tick', this.tickHandler);
        this.isTimerStarted = true;
    }

    public stopTimer(): void {
        if(!this.isTimerStarted) { return; }
        this.logger.info('Session Context Stopped');
        this.ticker.removeEventListener('tick', this.tickHandler);
        this.isTimerStarted = false;
    }

    private timerTick(time: number): void {

        if(!this.isLoggedIn()) {
            this.logger.warn('Session Expired');
            // session is expired
            this.clear('expire');
            return;
        }

        const timestamp = this.data.refresh && this.data.refresh.expiration || '';
        const now = new DateTimeValue(this.env.now());
        const other = new DateTimeValue(timestamp);
        const difference = other.difference(now, 'seconds');

        // convert minutes to seconds
        const warningThreshold = 60 * this.env.config.session.minutesToWarning;
        const dangerThreshold = 60 * this.env.config.session.minutesToDanger;

        const seconds = Math.max(0, difference); // set lower limit to 0
        let state: SessionExpirationState = 'invalid'

        if(difference < dangerThreshold) {
            state = 'danger';
        }
        else if(difference < warningThreshold) {
            state = 'warning';
        }
        else {
            state = 'valid';
        }

        if(state === 'danger' && !this.expirationEventIsAlerted) {
            this.events.publish(new SessionExpiringEvent({
                seconds,
                isAlerted: true,
                expiringType: 'critical'
            }));
            this.isSessionExpiring = true;
        } else if(!this.isLocked() && state === 'warning' && !this.expirationEventIsWarned) {
            this.events.publish(new SessionExpiringEvent({
                seconds,
                isAlerted: true,
                expiringType: 'warning'
            }));
            this.isSessionExpiring = true;
        } else if(!this.isLocked() && state === 'valid' && this.isSessionExpiring) {

            this.isSessionExpiring = false;
        }

        this.expirationEventIsWarned = state === 'warning' || state === 'danger';
        this.expirationEventIsAlerted = state === 'danger';
        this.expiration = { isAlerted: this.isSessionExpiring, timestamp, seconds, state };
    }
}
